import {
  actionTypeLabel,
  ALL_ACTION_TYPES_FILTER,
  appliedActionTypeFilter,
} from './action-types';

export type ActionSort = 'newest' | 'oldest' | 'name' | 'type' | 'modified';

/** Fields the configured-actions list reads when it filters and sorts. */
export interface ActionListEntry {
  key: string;
  actionId?: string;
  type: string;
  label: string;
  createdAt?: string;
  touchedAt?: number;
  catalogSummary: { updatedAt?: string } | null;
  /** Saved allowlist. Empty or absent means every division. */
  divisionIds?: readonly string[];
}

/** Accept ISO strings and epoch numbers; the toolbar has used both. */
function parseTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value) {
    const ts = Date.parse(value);
    if (Number.isFinite(ts)) return ts;
  }
  return 0;
}

function createdMs(action: ActionListEntry): number {
  return parseTimestamp(action.createdAt);
}

function modifiedMs(action: ActionListEntry): number {
  return Math.max(
    createdMs(action),
    parseTimestamp(action.catalogSummary?.updatedAt),
    action.touchedAt || 0,
  );
}

function typeSortLabel(action: ActionListEntry): string {
  return actionTypeLabel(action.type);
}

function compareActions(a: ActionListEntry, b: ActionListEntry, sortBy: ActionSort): number {
  if (sortBy === 'name') {
    return a.label.trim().localeCompare(b.label.trim(), undefined, { sensitivity: 'base' });
  }
  if (sortBy === 'type') {
    const byType = typeSortLabel(a).localeCompare(typeSortLabel(b), undefined, { sensitivity: 'base' });
    if (byType !== 0) return byType;
    return a.label.trim().localeCompare(b.label.trim(), undefined, { sensitivity: 'base' });
  }
  if (sortBy === 'modified') return modifiedMs(b) - modifiedMs(a);
  const byCreated = createdMs(b) - createdMs(a);
  return sortBy === 'oldest' ? -byCreated : byCreated;
}

/** View-only order. Unsaved drafts stay on top so an in-progress add is not buried. */
function sortActionsForView<T extends ActionListEntry>(actions: readonly T[], sortBy: ActionSort): T[] {
  const unsaved = actions.filter((action) => !action.actionId);
  const saved = actions.filter((action) => !!action.actionId);
  // When createdAt is missing, newest/oldest/modified compare equal. Use the
  // stored index so Oldest still reverses the list instead of looking dead.
  const indexByKey = new Map(actions.map((action, index) => [action.key, index]));
  return [
    ...unsaved,
    ...[...saved].sort((a, b) => {
      const primary = compareActions(a, b, sortBy);
      if (primary !== 0) return primary;
      const byIndex = (indexByKey.get(a.key) ?? 0) - (indexByKey.get(b.key) ?? 0);
      return sortBy === 'oldest' ? -byIndex : byIndex;
    }),
  ];
}

/** Select value that shows every configured action. Not a division id. */
export const ALL_DIVISIONS_FILTER = 'all';

export interface DivisionChoice {
  id: string;
  name: string;
}

/**
 * Divisions the filter may offer: current Genesys divisions that at least
 * one action names in `divisionIds`. An empty allowlist (every division)
 * adds nothing. A stored id that is not in `divisions` (deleted, or hidden
 * from this admin) is left out. Order is the division name, then id.
 */
export function divisionFilterOptions(
  actions: readonly { divisionIds?: readonly string[] }[],
  divisions: readonly DivisionChoice[],
): DivisionChoice[] {
  const used = new Set<string>();
  for (const action of actions) {
    for (const id of action.divisionIds ?? []) {
      if (id) used.add(id);
    }
  }
  if (used.size === 0) return [];
  return divisions
    .filter((division) => used.has(division.id))
    .sort((a, b) => {
      const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return byName !== 0 ? byName : a.id.localeCompare(b.id);
    });
}

/** A selection that is not in the submenu leaves the list unfiltered. */
export function appliedDivisionFilter(options: readonly { id: string }[], selected: string): string {
  if (options.length === 0 || !selected || selected === ALL_DIVISIONS_FILTER) return ALL_DIVISIONS_FILTER;
  return options.some((division) => division.id === selected) ? selected : ALL_DIVISIONS_FILTER;
}

export interface DivisionListFilter<T extends ActionListEntry> {
  selectedId: string;
  /** Membership. Defaults to `action.divisionIds` (the saved allowlist). */
  idsOf?: (action: T) => readonly string[];
  /**
   * Stay in the list even when the division does not match, so a card that
   * just left the division can still show its Saved confirmation.
   */
  keepKeys?: ReadonlySet<string>;
}

/**
 * View-only list. A type filter applies only when the org may use two or
 * more types. A division filter, when the selection is a real division id,
 * keeps actions whose allowlist names that id, plus actions with an empty
 * allowlist (every division, so that one too). The two filters compose.
 * Sort then orders that subset. Unsaved drafts stay on top.
 */
export function visibleConfiguredActions<T extends ActionListEntry>(
  actions: readonly T[],
  sortBy: ActionSort,
  allowedTypes: readonly string[],
  selectedType: string,
  division?: DivisionListFilter<T>,
): T[] {
  const type = appliedActionTypeFilter(allowedTypes, selectedType);
  const byType = type === ALL_ACTION_TYPES_FILTER ? actions : actions.filter((action) => action.type === type);
  const selected = division?.selectedId ?? ALL_DIVISIONS_FILTER;
  const filtered = !selected || selected === ALL_DIVISIONS_FILTER
    ? byType
    : byType.filter((action) => {
      if (division?.keepKeys?.has(action.key)) return true;
      const ids = division?.idsOf ? division.idsOf(action) : (action.divisionIds ?? []);
      return ids.length === 0 || ids.includes(selected);
    });
  return sortActionsForView(filtered, sortBy);
}
